import { Component, inject, signal } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModalLayoutComponent } from "../modal-layout/modal-layout.component";
import { ButtonComponent } from "../../button/button.component";
import { UI_FORM_IMPORTS } from '../../../ui-form-imports';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { LookupViewModel, LookupService, LookupStore } from '@program-suite/data-common';

@Component({
  selector: 'lib-update-producer-modal',
  imports: [
    ModalLayoutComponent,
    ButtonComponent,
    ...UI_FORM_IMPORTS,
    ReactiveFormsModule
],
  templateUrl: './update-producer-modal.component.html',
  styleUrl: './update-producer-modal.component.scss',
})
export class UpdateProducerModalComponent {
  data = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<UpdateProducerModalComponent>);
  lookupService = inject(LookupService);
  lookupStore = inject(LookupStore);

  agencyId = new UntypedFormControl('');
  producerId = new UntypedFormControl('');

  // Adhoc fields
  agencyName = new UntypedFormControl('');
  producerName = new UntypedFormControl('');
  agencyAddress1 = new UntypedFormControl('');
  agencyAddress2 = new UntypedFormControl('');
  agencyCity = new UntypedFormControl('');
  agencyStateCode = new UntypedFormControl(null);
  agencyZipCode = new UntypedFormControl('');

  agencies = signal<LookupViewModel[]>(this.data?.agencies || []);
  producers = signal<LookupViewModel[]>(this.data?.producers || []);
  adhocFieldsDisabled = signal<boolean>(false);

  hasRetailAgentId = !!this.data?.retailAgentId;
 

  constructor() {
    // Load states for adhoc fields
    this.lookupStore.loadLookup('states');

    if (this.hasRetailAgentId) {
      // Single dropdown for producers within the current agency
      this.producerId.setValue(this.data?.retailAgentId);
    } else {

      // Populate adhoc fields if data exists
      if (this.data?.agencyName && this.data?.agencyName != '-') {
        this.agencyName.setValue(this.data?.agencyName || '');
        this.producerName.setValue(this.data?.producerName || '');
        this.agencyAddress1.setValue(this.data?.agencyAddress1 || '');
        this.agencyAddress2.setValue(this.data?.agencyAddress2 || '');
        this.agencyCity.setValue(this.data?.agencyCity || '');
        this.agencyStateCode.setValue(this.data?.agencyStateCode || null);
        this.agencyZipCode.setValue(this.data?.agencyZipCode || '');
      }

      // Load producers when agency changes
      this.agencyId.valueChanges.subscribe(async (agencyId: number) => {
        if (agencyId) {
          try {
            const agents = await this.lookupService.getAgentsAndCSRs(agencyId);
            this.producers.set(agents);
            this.producerId.setValue(''); // Reset producer selection

            // Disable adhoc fields when agency is selected
            this.disableAdhocFields();
          } catch (error) {
            console.error('Error loading agents:', error);
            this.producers.set([]);
            this.producerId.setValue('');
          }
        } else {
          this.producers.set([]);
          this.producerId.setValue('');
          // Enable adhoc fields when agency is cleared
          this.enableAdhocFields();
        }
      });

      // Monitor adhoc fields - if any are filled, clear dropdown selections
      [this.agencyName, this.producerName, this.agencyAddress1, this.agencyCity, this.agencyStateCode, this.agencyZipCode].forEach(control => {
        control.valueChanges.subscribe(value => {
          if (value) {
            // Clear agency/producer dropdowns when adhoc fields are used
            this.agencyId.setValue('', { emitEvent: false });
            this.producerId.setValue('', { emitEvent: false });
          }
        });
      });
    }
  }

  disableAdhocFields() {
    this.agencyName.setValue('');
    this.producerName.setValue('');
    this.agencyAddress1.setValue('');
    this.agencyAddress2.setValue('');
    this.agencyCity.setValue('');
    this.agencyStateCode.setValue(null);
    this.agencyZipCode.setValue('');

    this.agencyName.disable();
    this.producerName.disable();
    this.agencyAddress1.disable();
    this.agencyAddress2.disable();
    this.agencyCity.disable();
    this.agencyStateCode.disable();
    this.agencyZipCode.disable();
    this.adhocFieldsDisabled.set(true);
  }

  enableAdhocFields() {
    this.agencyName.enable();
    this.producerName.enable();
    this.agencyAddress1.enable();
    this.agencyAddress2.enable();
    this.agencyCity.enable();
    this.agencyStateCode.enable();
    this.agencyZipCode.enable();
    this.adhocFieldsDisabled.set(false);
  }

  isFormValid(): boolean {
    if (this.hasRetailAgentId) {
      return !!this.producerId.value;
    }

    // Either agency/producer are selected OR adhoc fields are filled
    const hasDropdownSelection = !!this.producerId.value;
    const hasAdhocData = !!(
      this.agencyName.value &&
      this.producerName.value &&
      this.agencyAddress1.value &&
      this.agencyCity.value &&
      this.agencyStateCode.value &&
      this.agencyZipCode.value
    );

    return hasDropdownSelection || hasAdhocData;
  }

  onSubmit() {
    if (!this.isFormValid()) {
      // Mark all fields as touched to show validation errors
      [this.agencyId, this.producerId, this.agencyName, this.producerName,
       this.agencyAddress1, this.agencyCity, this.agencyStateCode, this.agencyZipCode].forEach(
        control => control.markAsTouched()
      );
      return;
    }

    const result: any = {};

    if (this.producerId.value) {
      // Using agency/producer dropdowns
      result.producerId = this.producerId.value;
    } else {
      // Using adhoc fields
      result.agencyName = this.agencyName.value;
      result.producerName = this.producerName.value;
      result.agencyAddress1 = this.agencyAddress1.value;
      result.agencyAddress2 = this.agencyAddress2.value;
      result.agencyCity = this.agencyCity.value;
      result.agencyStateCode = this.agencyStateCode.value;
      result.agencyZipCode = this.agencyZipCode.value;
    }

    this.dialogRef.close(result);
  }
}
